import axios from "axios";
import { WebAPI, IFailCallbackParam, AnyType } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

export interface FileChannelItem {
  chname: string;
  tagid: string;
  tagcomment: string;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでチャネルの取得が成功した場合に呼び出されます
   * @callback success
   * @param {FileChannelItem[]} channels - チャネル情報配列
   * @param {number} status - httpステータス
   */
  success: (channels: FileChannelItem[], status: number) => void;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

function createFileChannelItems(values: AnyType[]): FileChannelItem[] {
  const datas: FileChannelItem[] = [];
  let duplicate: string[] = [];
  values.forEach((value, i) => {
    const data: FileChannelItem[] = value.data;
    datas.push(...data);
    duplicate.push(
      ...data.map((val) => {
        return val.chname;
      })
    );
    if (i > 0) {
      duplicate = duplicate.filter((val, idx, arr) => {
        return arr.indexOf(val) === idx && idx !== arr.lastIndexOf(val);
      });
    }
  });
  const channels: FileChannelItem[] = [];
  duplicate.forEach((chname) => {
    const find = datas.find((val) => {
      return chname === val.chname;
    });
    if (find) {
      channels.push(find);
    }
  });
  return channels;
}

export interface IGetFileChannelsParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * ファイルID配列
   */
  fileids: string[];
}

/**
 * チャネルを取得します
 * @function getFileChannels
 * @param {IGetFileChannelsParams} params - チャネル取得パラメータ
 */
export function getFileChannels(params: IGetFileChannelsParams): void {
  const { authService, projectid, fileids } = params;
  if (authService) {
    const promises = fileids.map((fileid) => {
      return new Promise((resolve, reject) => {
        authService.getToken({
          success: (token) => {
            AxiosGet(WebAPI.CHANNELS(projectid, fileid), token)
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                reject(err);
              });
          },
          fail: (err) => {
            reject(err);
          },
        });
      });
    });
    Promise.all(promises)
      .then((values) => {
        params.success(createFileChannelItems(values), 200);
      })
      .catch((err) => {
        const { id, status } = getErrorStatus(err);
        if (status === 401) {
          getFileChannelsRetry401(authService, projectid, fileids, params);
        } else {
          params.fail(id, status);
        }
      });
  } else {
    const promises = fileids.map((fileid) => {
      return AxiosGet(WebAPI.CHANNELS(projectid, fileid));
    });
    Promise.all(promises)
      .then((values) => {
        params.success(createFileChannelItems(values), 200);
      })
      .catch((err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      });
  }
}

/**
 * チャネルを取得します
 * @function getFileChannels
 * @param {IGetFileChannelsParams} params - チャネル取得パラメータ
 */
export function getFileChannelsRetry401(
  authService: AuthService,
  projectid: string,
  fileids: string[],
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      const promises = fileids.map((fileid) => {
        return AxiosGet(WebAPI.CHANNELS(projectid, fileid), token);
      });
      Promise.all(promises)
        .then((values) => {
          params.success(createFileChannelItems(values), 200);
        })
        .catch((err) => {
          const { id, status } = getErrorStatus(err);
          params.fail(id, status);
        });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
